import { Request, Response } from "express";
import mongoose from "mongoose";

import { processUploadedFiles } from "../middleware/upload-middleware";
import { Course, School, Semester } from "../models";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class CourseController {
  /**
   * Create a new course (catalog entry)
   * @route POST /api/v1/school/courses
   * @access Private (School Admin)
   */
  async createCourse(req: AuthRequest, res: Response): Promise<void> {
    try {
      const {
        semesterId, // Required: Course must belong to a Semester
        courseCode,
        courseName,
        description,
        credits,
        prerequisites,
        department,
        level,
        courseType, // e.g. "Core", "Elective", "Lab"
        maxStudents, // Optional capacity
        learningOutcomes, // Array of strings/outcomes
        isActive = true, // Default: true (since school creates → immediately usable)
      } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Validate semesterId is provided
      if (!semesterId) {
        res.status(400).json({
          success: false,
          message:
            "Semester ID is required. Courses must be created within a semester context.",
          error: "Missing semesterId",
        } as APIResponse);
        return;
      }

      // Validate school exists
      const school = await School.findById(schoolId);
      if (!school) {
        res.status(404).json({
          success: false,
          message: "School not found",
          error: "School not found",
        } as APIResponse);
        return;
      }

      // Validate semester exists and belongs to school
      const semester = await Semester.findOne({
        _id: mongoose.Types.ObjectId.createFromHexString(semesterId),
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
      });

      if (!semester) {
        res.status(400).json({
          success: false,
          message:
            "Invalid semester ID or semester doesn't belong to your school",
          error: "Invalid semester",
        } as APIResponse);
        return;
      }

      // Check for duplicate course code within semester (not school)
      const existingCourse = await Course.findOne({
        semesterId: mongoose.Types.ObjectId.createFromHexString(semesterId),
        courseCode: courseCode.toUpperCase(),
      });

      if (existingCourse) {
        res.status(409).json({
          success: false,
          message: `A course with code "${courseCode}" already exists in this semester`,
          error: "Duplicate course code",
        } as APIResponse);
        return;
      }

      // Validate prerequisites if provided
      if (
        prerequisites &&
        Array.isArray(prerequisites) &&
        prerequisites.length > 0
      ) {
        const prerequisiteCourses = await Course.find({
          _id: { $in: prerequisites },
          schoolId: new mongoose.Types.ObjectId(schoolId),
        });

        if (prerequisiteCourses.length !== prerequisites.length) {
          res.status(400).json({
            success: false,
            message:
              "Some prerequisite courses are invalid or don't belong to your school",
            error: "Invalid prerequisites",
          } as APIResponse);
          return;
        }

        // Prevent self-referencing
        if (prerequisites.includes(req.body.courseId)) {
          res.status(400).json({
            success: false,
            message: "Course cannot be a prerequisite for itself",
            error: "Invalid prerequisites",
          } as APIResponse);
          return;
        }
      }

      // Parse learningOutcomes if sent as JSON string (multipart/form-data)
      let parsedLearningOutcomes: string[] | undefined;
      if (learningOutcomes !== undefined) {
        if (Array.isArray(learningOutcomes)) {
          parsedLearningOutcomes = learningOutcomes;
        } else if (typeof learningOutcomes === "string") {
          try {
            const parsed = JSON.parse(learningOutcomes);
            parsedLearningOutcomes = Array.isArray(parsed) ? parsed : [parsed];
          } catch {
            parsedLearningOutcomes = [learningOutcomes];
          }
        }
      }

      // Process syllabus file from multer (req.files when using uploadCourseSyllabus)
      const fileInfo = processUploadedFiles((req as any).files);
      const syllabusFile = fileInfo.syllabusFile;

      // Create course
      const course = new Course({
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        semesterId: mongoose.Types.ObjectId.createFromHexString(semesterId), // Required: Course belongs to Semester
        courseCode: courseCode.toUpperCase(),
        courseName,
        description,
        credits,
        prerequisites: prerequisites
          ? prerequisites.map((id: string) =>
              mongoose.Types.ObjectId.createFromHexString(id),
            )
          : undefined,
        department,
        level,
        courseType,
        maxStudents: maxStudents == null ? undefined : Number(maxStudents),
        learningOutcomes: parsedLearningOutcomes,
        syllabusFile,
        isActive: isActive !== false,
        createdBy: new mongoose.Types.ObjectId(userId),
      });

      await course.save();

      logger.info(`Course created: ${course.id}`, {
        courseCode: course.courseCode,
        courseName: course.courseName,
        schoolId,
        userId,
      });

      res.status(201).json({
        success: true,
        message: "Course created successfully",
        data: {
          course: {
            id: course.id,
            courseCode: course.courseCode,
            courseName: course.courseName,
            description: course.description,
            credits: course.credits,
            status: course.status,
            isActive: course.isActive,
            prerequisites: course.prerequisites,
            department: course.department,
            level: course.level,
            courseType: course.courseType,
            maxStudents: course.maxStudents,
            learningOutcomes: course.learningOutcomes,
            syllabusFile: course.syllabusFile,
            createdAt: course.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error creating course:", error);

      // Handle duplicate course code
      if (error.code === 11_000 || error.message.includes("unique")) {
        res.status(409).json({
          success: false,
          message: "A course with this code already exists in your school",
          error: "Duplicate course code",
        } as APIResponse);
        return;
      }

      if (error.message.includes("Course code must be unique")) {
        res.status(409).json({
          success: false,
          message: error.message,
          error: "Duplicate course code",
        } as APIResponse);
        return;
      }

      res.status(500).json({
        success: false,
        message: "Failed to create course",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all courses for a school
   * @route GET /api/v1/school/courses
   * @access Private (School Admin)
   */
  async getAllCourses(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;
      const {
        semesterId, // Optional: Filter by semester
        status,
        search,
        department,
        page = "1",
        limit = "20",
      } = req.query;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const pageNumber = Number.parseInt(page as string, 10);
      const limitNumber = Math.min(Number.parseInt(limit as string, 10), 100);
      const skip = (pageNumber - 1) * limitNumber;

      // Build query
      const query: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      // Filter by semester if provided
      if (semesterId) {
        query.semesterId = new mongoose.Types.ObjectId(semesterId as string);
      }

      if (status) {
        query.status = status;
      }

      if (department) {
        query.department = department;
      }

      if (search) {
        const searchRegex = new RegExp(search as string, "i");
        query.$or = [
          { courseCode: searchRegex },
          { courseName: searchRegex },
          { description: searchRegex },
        ];
      }

      // Get courses with pagination
      const courses = await Course.find({ ...query })
        .populate("prerequisites", "courseCode courseName")
        .sort({ courseCode: 1 })
        .skip(skip)
        .limit(limitNumber);

      const total = await Course.countDocuments({ ...query });

      logger.info(`Retrieved ${courses.length} courses for school ${schoolId}`);

      res.status(200).json({
        success: true,
        message: "Courses retrieved successfully",
        data: {
          courses: courses.map(course => ({
            id: course.id,
            courseCode: course.courseCode,
            courseName: course.courseName,
            description: course.description,
            credits: course.credits,
            status: course.status,
            isActive: course.isActive,
            prerequisites: course.prerequisites,
            department: course.department,
            level: course.level,
            courseType: course.courseType,
            maxStudents: course.maxStudents,
            learningOutcomes: course.learningOutcomes,
            syllabusFile: course.syllabusFile,
            createdAt: course.createdAt,
          })),
          pagination: {
            page: pageNumber,
            limit: limitNumber,
            total,
            pages: Math.ceil(total / limitNumber),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error retrieving courses:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve courses",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get course by ID
   * @route GET /api/v1/school/courses/:id
   * @access Private (School Admin)
   */
  async getCourseById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const course = await Course.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      }).populate("prerequisites", "courseCode courseName");

      if (!course) {
        res.status(404).json({
          success: false,
          message: "Course not found",
          error: "Course not found",
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: "Course retrieved successfully",
        data: {
          course: {
            id: course.id,
            courseCode: course.courseCode,
            courseName: course.courseName,
            description: course.description,
            credits: course.credits,
            status: course.status,
            isActive: course.isActive,
            prerequisites: course.prerequisites,
            department: course.department,
            level: course.level,
            courseType: course.courseType,
            maxStudents: course.maxStudents,
            learningOutcomes: course.learningOutcomes,
            syllabusFile: course.syllabusFile,
            createdAt: course.createdAt,
            updatedAt: course.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error retrieving course:", error);

      if (error.name === "CastError") {
        res.status(400).json({
          success: false,
          message: "Invalid course ID",
          error: "Invalid course ID",
        } as APIResponse);
        return;
      }

      res.status(500).json({
        success: false,
        message: "Failed to retrieve course",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update course
   * @route PUT /api/v1/school/courses/:id
   * @access Private (School Admin)
   */
  async updateCourse(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const {
        courseCode,
        courseName,
        description,
        credits,
        prerequisites,
        department,
        level,
        status,
        isActive,
        courseType,
        maxStudents,
        learningOutcomes,
      } = req.body;
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const course = await Course.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!course) {
        res.status(404).json({
          success: false,
          message: "Course not found",
          error: "Course not found",
        } as APIResponse);
        return;
      }

      // Check for duplicate course code if code is being updated
      if (courseCode && courseCode.toUpperCase() !== course.courseCode) {
        const existingCourse = await Course.findOne({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          courseCode: courseCode.toUpperCase(),
          _id: { $ne: id },
        });

        if (existingCourse) {
          res.status(409).json({
            success: false,
            message: `A course with code "${courseCode}" already exists in your school`,
            error: "Duplicate course code",
          } as APIResponse);
          return;
        }

        course.courseCode = courseCode.toUpperCase();
      }

      // Validate prerequisites if provided
      if (
        prerequisites &&
        Array.isArray(prerequisites) &&
        prerequisites.length > 0
      ) {
        const prerequisiteCourses = await Course.find({
          _id: { $in: prerequisites },
          schoolId: new mongoose.Types.ObjectId(schoolId),
        });

        if (prerequisiteCourses.length !== prerequisites.length) {
          res.status(400).json({
            success: false,
            message:
              "Some prerequisite courses are invalid or don't belong to your school",
            error: "Invalid prerequisites",
          } as APIResponse);
          return;
        }

        // Prevent self-referencing
        if (prerequisites.includes(id)) {
          res.status(400).json({
            success: false,
            message: "Course cannot be a prerequisite for itself",
            error: "Invalid prerequisites",
          } as APIResponse);
          return;
        }
      }

      // Update fields
      if (courseName !== undefined) course.courseName = courseName;
      if (description !== undefined) course.description = description;
      if (credits !== undefined) course.credits = credits;
      if (prerequisites !== undefined) {
        course.prerequisites = prerequisites
          ? prerequisites.map((prereqId: string) =>
              mongoose.Types.ObjectId.createFromHexString(prereqId),
            )
          : [];
      }
      if (department !== undefined) course.department = department;
      if (level !== undefined) course.level = level;
      if (status !== undefined) course.status = status;
      if (isActive !== undefined) course.isActive = isActive;
      if (courseType !== undefined) course.courseType = courseType;
      if (maxStudents !== undefined) course.maxStudents = maxStudents;
      if (learningOutcomes !== undefined)
        course.learningOutcomes = learningOutcomes;

      await course.save();

      logger.info(`Course updated: ${course.id}`, {
        courseCode: course.courseCode,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Course updated successfully",
        data: {
          course: {
            id: course.id,
            courseCode: course.courseCode,
            courseName: course.courseName,
            description: course.description,
            credits: course.credits,
            status: course.status,
            isActive: course.isActive,
            prerequisites: course.prerequisites,
            department: course.department,
            level: course.level,
            courseType: course.courseType,
            maxStudents: course.maxStudents,
            learningOutcomes: course.learningOutcomes,
            syllabusFile: course.syllabusFile,
            updatedAt: course.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error updating course:", error);

      if (error.name === "CastError") {
        res.status(400).json({
          success: false,
          message: "Invalid course ID",
          error: "Invalid course ID",
        } as APIResponse);
        return;
      }

      if (error.code === 11_000 || error.message.includes("unique")) {
        res.status(409).json({
          success: false,
          message: "A course with this code already exists in your school",
          error: "Duplicate course code",
        } as APIResponse);
        return;
      }

      res.status(500).json({
        success: false,
        message: "Failed to update course",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Delete course
   * @route DELETE /api/v1/school/courses/:id
   * @access Private (School Admin)
   */
  async deleteCourse(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const course = await Course.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!course) {
        res.status(404).json({
          success: false,
          message: "Course not found",
          error: "Course not found",
        } as APIResponse);
        return;
      }

      // Check if course is used in any classes
      const Class = mongoose.model("Class");
      const classesUsingCourse = await Class.countDocuments({
        courseId: new mongoose.Types.ObjectId(id),
      });

      if (classesUsingCourse > 0) {
        res.status(409).json({
          success: false,
          message: `Cannot delete course. It is being used in ${classesUsingCourse} class(es). Please remove all classes using this course first.`,
          error: "Course in use",
        } as APIResponse);
        return;
      }

      await course.deleteOne();

      logger.info(`Course deleted: ${course.id}`, {
        courseCode: course.courseCode,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Course deleted successfully",
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error deleting course:", error);

      if (error.name === "CastError") {
        res.status(400).json({
          success: false,
          message: "Invalid course ID",
          error: "Invalid course ID",
        } as APIResponse);
        return;
      }

      res.status(500).json({
        success: false,
        message: "Failed to delete course",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get course statistics
   * @route GET /api/v1/school/courses/statistics
   * @access Private (School Admin)
   */
  async getCourseStatistics(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const statistics = await Course.getStatistics(
        new mongoose.Types.ObjectId(schoolId),
      );

      res.status(200).json({
        success: true,
        message: "Course statistics retrieved successfully",
        data: {
          statistics,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error retrieving course statistics:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve course statistics",
        error: "Internal server error",
      } as APIResponse);
    }
  }
}

// Export singleton instance
export const courseController = new CourseController();
